import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  ClientesPorStatus,
  TempoMedio,
  DesempenhoAtendente,
  getStatusConfig,
} from '@/types/dashboard';
import { getStatusOSLabels } from '@/types/ordemServico';
import { useOrganization } from '@/hooks/useOrganization';
import {
  fetchClientesStatus,
  fetchClientesFinalizadosDesde,
  fetchClientesComResponsavel,
  fetchProfilesByIds,
  fetchOSStatus,
  fetchOSComPrestador,
  fetchPrestadoresByIds,
  agruparClientesPorStatus,
  calcularTempoMedioPorDiaSemana,
  agruparDesempenhoAtendentes,
  agruparOSPorStatus,
  agruparDesempenhoPrestadores,
  type OSPorStatus,
  type DesempenhoPrestador,
} from '@/services/dashboard.service';

type Periodo = '7d' | '30d' | '90d';

// Re-export types for backward compatibility with existing consumers
export type { OSPorStatus, DesempenhoPrestador };

export function useClientesPorStatus() {
  const { isAdmin, profile } = useAuthContext();
  const { t } = useTranslation();
  const STATUS_CONFIG = getStatusConfig(t);
  const { organizationId } = useOrganization();

  return useQuery<ClientesPorStatus[]>({
    queryKey: ['dashboard', organizationId, 'status', { isAdmin, profileId: profile?.id }],
    queryFn: async () => {
      const clientes = await fetchClientesStatus();
      return agruparClientesPorStatus(clientes, STATUS_CONFIG);
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useTempoMedioAtendimento(periodo: Periodo = '7d') {
  const { isAdmin, profile } = useAuthContext();
  const { organizationId } = useOrganization();

  return useQuery<TempoMedio[]>({
    queryKey: ['dashboard', organizationId, 'tempo-medio', periodo, { isAdmin, profileId: profile?.id }],
    queryFn: async () => {
      const diasAtras = periodo === '7d' ? 7 : periodo === '30d' ? 30 : 90;
      const dataInicio = new Date();
      dataInicio.setDate(dataInicio.getDate() - diasAtras);
      const clientes = await fetchClientesFinalizadosDesde(dataInicio);
      return calcularTempoMedioPorDiaSemana(clientes);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useDesempenhoAtendentes() {
  const { isAdmin, profile } = useAuthContext();
  const { organizationId } = useOrganization();

  return useQuery<DesempenhoAtendente[]>({
    queryKey: ['dashboard', organizationId, 'desempenho', { isAdmin, profileId: profile?.id }],
    queryFn: async () => {
      const clientes = await fetchClientesComResponsavel();
      const responsavelIds = [...new Set(clientes.map(c => c.responsavel_id).filter((id): id is string => !!id))];
      const profiles = await fetchProfilesByIds(responsavelIds);
      const filterByProfileId = !isAdmin && profile ? profile.id : undefined;
      return agruparDesempenhoAtendentes(clientes, profiles, filterByProfileId);
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useOSPorStatus() {
  const { isAdmin } = useAuthContext();
  const { t } = useTranslation();
  const STATUS_OS_LABELS = getStatusOSLabels(t);
  const { organizationId } = useOrganization();

  return useQuery<OSPorStatus[]>({
    queryKey: ['dashboard', organizationId, 'os-status', { isAdmin }],
    queryFn: async () => {
      const ordens = await fetchOSStatus();
      return agruparOSPorStatus(ordens, STATUS_OS_LABELS);
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useDesempenhoPrestadores() {
  const { isAdmin } = useAuthContext();
  const { organizationId } = useOrganization();

  return useQuery<DesempenhoPrestador[]>({
    queryKey: ['dashboard', organizationId, 'desempenho-prestadores', { isAdmin }],
    queryFn: async () => {
      const ordens = await fetchOSComPrestador();
      const prestadorIds = [...new Set(ordens.map(os => os.prestador_id).filter((id): id is string => !!id))];
      const prestadores = await fetchPrestadoresByIds(prestadorIds);
      return agruparDesempenhoPrestadores(ordens, prestadores);
    },
    staleTime: 2 * 60 * 1000,
    enabled: isAdmin,
  });
}
